import React, { useState } from 'react';
import { SearchContext } from './SearchContext';
import { useSearch } from './useSearch';
import { GearSearchQueryRequest } from '../../Model/GearSearchQueryRequest';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { useTriggerExportToCsv } from './useSearchExport';
import { useNotificationContext } from '../../global/hooks/useNotificationContext';
import { GearSearchExportRequest } from '../../Model/GearSearchExportRequest';
import { useCurrentUserContext } from '../currentUser/CurrentUserContext';
import { useGlobalizationContext } from '../globalization/GlobalizationContext';
import { useTranslation } from '../globalization/useTranslation';

interface ViewsProviderProps {
  children?: React.ReactNode;
}

export const SearchProvider: React.FC<ViewsProviderProps> = ({ children }) => {
  const { selectedIndex } = useCurrentUserContext();
  const [queryRequest, setQueryRequest] = useState<GearSearchQueryRequest | undefined>(undefined);
  const { data, isLoading } = useSearch(queryRequest);
  const [compositeFilterDescriptor, setCompositeFilterDescriptor] = useState<CompositeFilterDescriptor | undefined>();
  const { mutate: mutateTriggerExportToCsv } = useTriggerExportToCsv();
  const { showToast } = useNotificationContext();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);

  React.useEffect(() => {
    setQueryRequest(undefined);
    setCompositeFilterDescriptor(undefined);
  }, [selectedIndex?.id]);

  const handleTriggerExportToCsv = (dto: GearSearchExportRequest): void => {
    mutateTriggerExportToCsv(
      { dto: { ...dto, indexId: selectedIndex?.id } },
      {
        onSuccess: () => {
          showToast({
            type: 'success',
            message: translate('custom.notifications.exportCsv')
          });
        }
      }
    );
  };

  return (
    <SearchContext.Provider
      value={{
        data,
        isLoading,
        queryRequest,
        setQueryRequest,
        compositeFilterDescriptor,
        setCompositeFilterDescriptor,
        handleTriggerExportToCsv
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};
